package ru.dinarastepina.fabvocab.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import ru.dinarastepina.fabvocab.domain.model.WordEntry
import ru.dinarastepina.fabvocab.web.api.RemoteWordRepository
import ru.dinarastepina.fabvocab.web.api.VocabularyApiClient
import ru.dinarastepina.fabvocab.web.components.EmptyState
import ru.dinarastepina.fabvocab.web.components.TopBar
import ru.dinarastepina.fabvocab.web.components.WordCard
import ru.dinarastepina.fabvocab.web.theme.IndigoPalette
import ru.dinarastepina.fabvocab.web.theme.Rose600
import ru.dinarastepina.fabvocab.web.theme.SlatePalette
import ru.dinarastepina.fabvocab.web.theme.Slate500

@Page
@Composable
fun HomePage() {
    val ctx = rememberPageContext()
    val client = remember { VocabularyApiClient() }
    val repo = remember { RemoteWordRepository(client) }
    val wordList = remember { mutableStateOf(emptyList<WordEntry>()) }
    val isLoading = remember { mutableStateOf(true) }
    val error = remember { mutableStateOf<String?>(null) }
    val loggedIn = remember { mutableStateOf(client.isLoggedIn()) }

    LaunchedEffect(Unit) {
        if (client.isLoggedIn()) {
            try {
                repo.refresh()
                repo.observeWords().collect {
                    wordList.value = it
                    isLoading.value = false
                }
            } catch (e: Exception) {
                error.value = e.message
                isLoading.value = false
            }
        } else {
            isLoading.value = false
        }
    }

    Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
        TopBar(
            title = "FabVocab",
            actions = {
                if (loggedIn.value) {
                    if (!isLoading.value && wordList.value.isNotEmpty()) {
                        Button(
                            onClick = { ctx.router.navigateTo("/practice") },
                            colorPalette = IndigoPalette,
                            size = ButtonSize.SM,
                        ) {
                            SpanText("Practice", modifier = Modifier.fontWeight(FontWeight.Bold))
                        }
                    }
                    Button(
                        onClick = {
                            client.logout()
                            loggedIn.value = false
                            wordList.value = emptyList()
                        },
                        colorPalette = SlatePalette,
                        size = ButtonSize.SM,
                    ) {
                        SpanText("Logout", modifier = Modifier.fontSize(12.px))
                    }
                } else {
                    Button(
                        onClick = { ctx.router.navigateTo("/login") },
                        colorPalette = IndigoPalette,
                        size = ButtonSize.SM,
                    ) {
                        SpanText("Sign In", modifier = Modifier.fontWeight(FontWeight.Bold))
                    }
                }
            },
        )

        if (loggedIn.value) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    onClick = { ctx.router.navigateTo("/add") },
                    colorPalette = IndigoPalette,
                    size = ButtonSize.SM,
                ) {
                    SpanText("Add Word", modifier = Modifier.fontWeight(FontWeight.Bold))
                }
            }
        }

        when {
            !loggedIn.value -> {
                EmptyState(
                    title = "Sign in to start",
                    subtitle = "Create an account or sign in to build your vocabulary.",
                    modifier = Modifier.padding(top = 48.px),
                )
            }
            isLoading.value -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(48.px),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        "Loading...",
                        modifier = Modifier.color(Slate500).fontSize(14.px),
                    )
                }
            }
            error.value != null -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.px),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        error.value!!,
                        modifier = Modifier
                            .color(Rose600)
                            .fontWeight(FontWeight.SemiBold)
                            .fontSize(14.px),
                    )
                }
            }
            wordList.value.isEmpty() -> {
                EmptyState(
                    title = "No words yet",
                    subtitle = "Tap Add Word to fetch a Learner's Dictionary entry and save it.",
                    modifier = Modifier.padding(top = 48.px),
                )
            }
            else -> {
                SpanText(
                    "Your words (${wordList.value.size}):",
                    modifier = Modifier
                        .padding(leftRight = 4.px)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.SemiBold)
                        .color(Slate500),
                )
                wordList.value.forEach { word ->
                    WordCard(
                        word = word,
                        onClick = { ctx.router.navigateTo("/words/${word.id}") },
                    )
                }
            }
        }
    }
}
